import { Typography } from '@mui/material/styles/createTypography';

import { MOBILE_MAX_WIDTH } from './breakpoints';
import { FONT_SIZE_BODY, FONT_SIZE_HTML } from './consts';
import { lineHeightPxToUnitless, pxToRem } from './helpers';

// The Tradehaus type roles. Titles are light and tightly tracked; controls (field, button) are 600; body and labels
// are 500. Phones get larger body, button and field text (16px fields stop iOS zooming on focus).
const FONT_FAMILY = 'Manrope, sans-serif';

const FONT_WEIGHT_LIGHT = 300;
const FONT_WEIGHT_REGULAR = 400;
const FONT_WEIGHT_MEDIUM = 500;
const FONT_WEIGHT_SEMIBOLD = 600;
const FONT_WEIGHT_BOLD = 700;

const PHONE = `@media (max-width:${MOBILE_MAX_WIDTH}px)`;

const title = (px: number, lineHeightPx: number, phonePx?: number) => ({
  fontFamily: FONT_FAMILY,
  fontWeight: FONT_WEIGHT_LIGHT,
  fontSize: pxToRem(px),
  lineHeight: lineHeightPxToUnitless(lineHeightPx, px),
  letterSpacing: '-0.035em',
  ...(phonePx && { [PHONE]: { fontSize: pxToRem(phonePx) } }),
});

const text = (weight: number, px: number, lineHeightPx: number, phonePx?: number) => ({
  fontFamily: FONT_FAMILY,
  fontWeight: weight,
  fontSize: pxToRem(px),
  lineHeight: lineHeightPxToUnitless(lineHeightPx, px),
  ...(phonePx && { [PHONE]: { fontSize: pxToRem(phonePx) } }),
});

const control = (px: number, phonePx: number) => ({
  fontFamily: FONT_FAMILY,
  fontWeight: FONT_WEIGHT_SEMIBOLD,
  fontSize: pxToRem(px),
  lineHeight: 1,
  letterSpacing: '-0.02em',
  textTransform: 'none' as const,
  [PHONE]: { fontSize: pxToRem(phonePx) },
});

export const typography: Partial<Typography> = {
  htmlFontSize: FONT_SIZE_HTML,
  fontSize: FONT_SIZE_BODY,
  pxToRem,

  fontFamily: FONT_FAMILY,
  fontWeightLight: FONT_WEIGHT_LIGHT,
  fontWeightRegular: FONT_WEIGHT_REGULAR,
  fontWeightMedium: FONT_WEIGHT_MEDIUM,
  fontWeightBold: FONT_WEIGHT_BOLD,

  h1: title(48, 54, 36),
  h2: title(40, 46, 32),
  // Page title
  h3: title(34, 40, 30),
  // Title: a sheet or dialog's heading
  h4: title(28, 32),
  h5: { ...title(22, 28), letterSpacing: '-0.03em' },
  // Card head
  h6: { ...text(FONT_WEIGHT_REGULAR, 18, 24), letterSpacing: '-0.02em' },

  subtitle1: text(FONT_WEIGHT_SEMIBOLD, 14, 20),
  // Label: over a field or a group
  subtitle2: text(FONT_WEIGHT_MEDIUM, 12, 16),

  body1: text(FONT_WEIGHT_MEDIUM, 14, 21, 15),
  // Body
  body2: text(FONT_WEIGHT_MEDIUM, 13, 19, 14),
  body3: text(FONT_WEIGHT_MEDIUM, 13, 19, 14),
  // Meta
  body4: text(FONT_WEIGHT_MEDIUM, 11, 16),
  caption: text(FONT_WEIGHT_MEDIUM, 11, 16),

  // Eyebrow: never uppercase, never spaced out
  overline: {
    ...text(FONT_WEIGHT_SEMIBOLD, 12, 16),
    textTransform: 'none',
    letterSpacing: 0,
  },

  button: control(13, 14),
  buttonMedium: control(13, 14),
  buttonSmall: control(13, 13),

  buttonIconMedium: {
    fontSize: 18,
  },
  buttonIconSmall: {
    fontSize: 16,
  },

  // Field
  inputMedium: text(FONT_WEIGHT_SEMIBOLD, 13, 20, 16),
  inputSmall: text(FONT_WEIGHT_SEMIBOLD, 13, 18, 16),

  // Hint under a field
  inputHelperMedium: text(FONT_WEIGHT_MEDIUM, 12, 16),
  inputHelperSmall: text(FONT_WEIGHT_MEDIUM, 11, 16),

  cardTitle: { ...text(FONT_WEIGHT_REGULAR, 18, 24), letterSpacing: '-0.02em' },
};
